import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User as UserIcon,
  Video,
  CheckCircle2,
  XCircle,
} from "lucide-react";

interface ClassRow {
  id: string;
  grade: number;
  subject: string | null;
  title: string;
  description: string | null;
  teacherName: string;
  scheduledAt: string;
  durationMinutes: number;
  meetingUrl: string | null;
  status: "upcoming" | "live" | "ended" | "cancelled";
  recordingUrl: string | null;
  isRegistered: boolean;
}

interface QuestionRow {
  id: string;
  questionText: string;
  options: any;
  questionType: string;
  correctAnswer?: string; // omitted server-side until submission
}

interface QuizPayload {
  assignment: {
    id: string;
    questionIds: string[];
    completedAt: string | null;
    score: number | null;
    correctCount: number | null;
    totalCount: number | null;
  };
  questions: QuestionRow[];
}

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/Jamaica",
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function isJoinable(cls: ClassRow): boolean {
  if (cls.status !== "upcoming" && cls.status !== "live") return false;
  if (!cls.meetingUrl) return false;
  const start = new Date(cls.scheduledAt).getTime();
  const end = start + cls.durationMinutes * 60 * 1000;
  const now = Date.now();
  return now >= start - 10 * 60 * 1000 && now <= end;
}

export default function ClassDetail() {
  const [, params] = useRoute<{ id: string }>("/classes/:id");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const id = params?.id;

  // Pull the full classes list and pick out the one we want: keeps cache shared
  // with /classes and avoids a per-id endpoint.
  const { data: classes = [], isLoading } = useQuery<ClassRow[]>({
    queryKey: ["/api/live-classes"],
    queryFn: async () => (await apiRequest("GET", "/api/live-classes")).json(),
    retry: false,
  });
  const cls = classes.find((c) => c.id === id) || null;

  const [showQuiz, setShowQuiz] = useState(false);

  const register = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/live-classes/${id}/register`, {})).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-classes"] });
      toast({ title: "You're registered" });
    },
    onError: (e: any) => toast({ title: "Registration failed", description: e?.message, variant: "destructive" }),
  });

  const unregister = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", `/api/live-classes/${id}/register`)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-classes"] });
      toast({ title: "Unregistered" });
    },
  });

  const join = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/live-classes/${id}/join`, {})).json(),
    onSuccess: (data: { meetingUrl?: string }) => {
      if (data.meetingUrl) window.open(data.meetingUrl, "_blank");
    },
    onError: (e: any) => toast({ title: "Could not open class", description: e?.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="container mx-auto p-6">Loading…</div>;

  if (!cls) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Button variant="ghost" onClick={() => navigate("/classes")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Classes
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Class not found</CardTitle>
            <CardDescription>This class doesn't exist or you don't have access to it.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const upcoming = cls.status === "upcoming" || cls.status === "live";
  const ended = cls.status === "ended";

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-3xl">
      <Button variant="ghost" onClick={() => navigate("/classes")} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Classes
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge variant="outline">Grade {cls.grade}</Badge>
            {cls.subject && <Badge variant="outline">{cls.subject}</Badge>}
            {cls.status === "live" && <Badge className="bg-red-600 text-white border-red-700">Live now</Badge>}
            {cls.status === "ended" && <Badge variant="secondary">Ended</Badge>}
            {cls.isRegistered && <Badge className="bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30">Registered</Badge>}
          </div>
          <CardTitle className="text-xl md:text-2xl">{cls.title}</CardTitle>
          {cls.description && <CardDescription>{cls.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><UserIcon className="h-4 w-4 text-muted-foreground" /> {cls.teacherName}</div>
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /> {formatLocal(cls.scheduledAt)}</div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> {cls.durationMinutes} minutes</div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {upcoming && !cls.isRegistered && (
              <Button onClick={() => register.mutate()} disabled={register.isPending}>Register</Button>
            )}
            {upcoming && cls.isRegistered && (
              <>
                {isJoinable(cls) && (
                  <Button onClick={() => join.mutate()} className="bg-blue-500 hover:bg-blue-600">
                    <Video className="h-4 w-4 mr-2" /> Join Class
                  </Button>
                )}
                <Button variant="outline" onClick={() => unregister.mutate()} disabled={unregister.isPending}>
                  Unregister
                </Button>
              </>
            )}
            {ended && cls.isRegistered && (
              <Button onClick={() => setShowQuiz(true)}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Take post-class quiz
              </Button>
            )}
            {cls.recordingUrl && (
              <Button variant="outline" onClick={() => window.open(cls.recordingUrl!, "_blank")}>
                <Video className="h-4 w-4 mr-2" /> Watch recording
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {showQuiz && cls.isRegistered && <PostClassQuiz classId={cls.id} onDone={() => setShowQuiz(false)} />}
    </div>
  );
}

// ── Inline post-class quiz ─────────────────────────────────────────────────
function PostClassQuiz({ classId, onDone }: { classId: string; onDone: () => void }) {
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState<{ score: number; correctCount: number; totalCount: number } | null>(null);

  const { data, isLoading, error } = useQuery<QuizPayload>({
    queryKey: [`/api/live-classes/${classId}/quiz`],
    queryFn: async () => (await apiRequest("GET", `/api/live-classes/${classId}/quiz`)).json(),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", `/api/live-classes/${classId}/quiz/submit`, { answers })).json(),
    onSuccess: (res: any) => {
      setSubmitted({ score: res.score, correctCount: res.correctCount, totalCount: res.totalCount });
    },
    onError: (e: any) => toast({ title: "Submit failed", description: e?.message, variant: "destructive" }),
  });

  if (isLoading) return <Card><CardContent className="p-6">Loading quiz…</CardContent></Card>;

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Quiz not available</CardTitle>
          <CardDescription>{(error as any)?.message || "Quiz could not be loaded."}</CardDescription>
        </CardHeader>
        <CardContent><Button variant="outline" onClick={onDone}>Close</Button></CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" /> Quiz complete
          </CardTitle>
          <CardDescription>
            You got {submitted.correctCount} of {submitted.totalCount} correct ({submitted.score}%).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onDone}>Back to class</Button>
        </CardContent>
      </Card>
    );
  }

  const qs = data?.questions || [];
  const completed = !!data?.assignment?.completedAt;

  if (completed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>You've already completed this quiz</CardTitle>
          <CardDescription>
            Score: {data!.assignment.score}% ({data!.assignment.correctCount}/{data!.assignment.totalCount})
          </CardDescription>
        </CardHeader>
        <CardContent><Button variant="outline" onClick={onDone}>Close</Button></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Post-class quiz</CardTitle>
        <CardDescription>{qs.length} questions. Take your time.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {qs.map((q, idx) => (
          <div key={q.id} className="space-y-2">
            <p className="font-medium">{idx + 1}. {q.questionText}</p>
            {q.options && Array.isArray(q.options) ? (
              <div className="space-y-2">
                {q.options.map((opt: any, oi: number) => {
                  const value = typeof opt === "string" ? opt : (opt.text || JSON.stringify(opt));
                  return (
                    <label key={oi} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded p-2">
                      <input
                        type="radio"
                        name={q.id}
                        value={value}
                        checked={answers[q.id] === value}
                        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      />
                      <span className="text-sm">{value}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <input
                type="text"
                className="w-full border rounded px-3 py-2 text-sm"
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                placeholder="Your answer"
              />
            )}
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => submit.mutate()}
            disabled={submit.isPending || Object.keys(answers).length === 0}
          >
            Submit quiz
          </Button>
          <Button variant="outline" onClick={onDone}>
            <XCircle className="h-4 w-4 mr-2" /> Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
